import { useForm } from '@nerdgeschoss/react-use-form-library';
import React from 'react';

import { Button } from '../../../components/Button/Button';
import { Card } from '../../../components/Card/Card';
import { ContributionListChart } from '../../../components/Charts/contribution_list_chart';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { NoData } from '../../../components/NoData/NoData';
import { Stack } from '../../../components/Stack/Stack';
import { Text } from '../../../components/text/text';
import { graphql } from '../../../generated';
import { StatisticsProductsStatistic } from '../../../generated/graphql';
import { t } from '../../../locales/translate';
import { productPath } from '../../../routes';
import { formatCurrency, formatNumber } from '../../../util/formatters';
import { withSuspendedCard } from '../../../util/suspense';
import { useSuspenseQuery } from '../../../util/use_suspense_query';
import { Spinner } from '../../../components/Spinner/Spinner';

const DASHBOARD_PRODUCTS = graphql(`
  query DashboardProducts(
    $from: ISO8601DateTime
    $to: ISO8601DateTime
    $statistic: StatisticsProductsStatistic
    $first: Int
    $after: String
  ) {
    statistics {
      products(during: { from: $from, to: $to }, statistic: $statistic, first: $first, after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            product {
              artist
              name
              id
              slug
              sku
              ... on MerchProduct {
                color
                productType
              }
            }
            absolute
            percentage
          }
        }
      }
    }
  }
`);

interface Props {
  from: Date;
  to: Date;
}

function Products$({ from, to }: Props): JSX.Element | null {
  const { model, fields } = useForm<{
    statistic: StatisticsProductsStatistic;
  }>({
    model: {
      statistic: 'TURNOVER',
    },
  });

  const { data, fetchMore, loadingMore } = useSuspenseQuery(DASHBOARD_PRODUCTS, {
    variables: {
      from,
      to,
      statistic: model.statistic,
      first: 5,
    },
  });

  const sessionsData = data.statistics?.products.edges.map((edge) => edge.node) || [];
  const chartData =
    sessionsData?.map((item) => ({
      percentage: item.percentage,
      label: [
        item.product.sku,
        item.product.artist,
        item.product.__typename === 'MerchProduct' ? item.product.productType : null,
        item.product.name,
        item.product.__typename === 'MerchProduct' ? item.product.color : null,
      ]
        .filter((v) => v !== null)
        .join(' - '),
      value: model.statistic === 'TURNOVER' ? formatCurrency(item.absolute) : formatNumber(item.absolute),
      href: productPath(item.product.id),
    })) || [];

  return (
    <Card
      size="dashboard"
      title={<p>{t.component.products.title}</p>}
      actions={
        <FormSelect
          {...fields.statistic}
          fullBorder
          icon="filter"
          options={[
            {
              value: 'TURNOVER',
              label: t.component.products.turnover,
            },
            {
              value: 'SALES',
              label: t.component.products.sales,
            },
          ]}
          name="statistic"
        />
      }
      footer={
        <Stack fullWidth justify="center">
          {sessionsData?.length && loadingMore ? (
            <Spinner />
          ) : (
            data.statistics?.products.pageInfo.hasNextPage && (
              <Button
                onClick={() => {
                  fetchMore({
                    variables: {
                      after: data?.statistics?.products.pageInfo.endCursor,
                    },
                  });
                }}
                type="secondary-dark"
                title={t.actions.loadMore}
              />
            )
          )}
        </Stack>
      }
    >
      {sessionsData?.length ? (
        <Stack size="md">
          <Text type="dashboard-card-label">
            {model.statistic === 'TURNOVER' ? t.component.products.turnover : t.component.products.sales}
          </Text>
          <ContributionListChart data={chartData} />
        </Stack>
      ) : (
        <NoData description={t.component.products.noData} />
      )}
    </Card>
  );
}

export const Products = withSuspendedCard(Products$, { loadingTitle: t.component.products.title, loadingHeight: 452 });
